import React from "react";
import { getSsrSnapshotSubtreeHtml } from "@/main/react/ssrSnapshot";

/**
 * The profile's server-rendered trophy row, put back on screen while the rail cannot render its own.
 *
 * WHY THIS EXISTS (rule 32, 2026-09-10). `user.jte` ships the earned rungs as `profile-achievements-ssr`
 * inside `data-component="UserPage"`, and `createRoot()` discards that container's children when UserPage
 * mounts, before the rail's chunk or its fetch resolve. So the row was gone for every load in between,
 * and for good when the fetch failed: a member's earned rungs vanished from their public profile with no
 * marker anywhere. Design: designs/01-now/2026-07-27-achievements-system-layers-tiers-and-award-model-design.md §9.2.
 *
 * `restored` is true only after a TERMINAL failure (the rail's fetch, or since FI-070 its chunk), and only then does the row carry
 * `data-island-achievements-restored`, which must byte-match `ISLAND_ACHIEVEMENTS_RESTORED_SELECTOR` in
 * the Playwright `tests/helpers/islandHealthGuard.mjs` (no compile-time link). While a healthy load is
 * merely pending the row is unmarked, or every lane would go red on a slow network.
 */

const USER_PAGE_COMPONENT = "UserPage";
const SERVER_ROW_SELECTOR = '[data-playwright-id="profile-achievements-ssr"]';
const PROFILE_HEADING_SELECTOR = ".user-overview h1.username";

function normalizeHandle(value: string): string {
    return value.trim().replace(/^@/, "").toLowerCase();
}

function textOf(html: string): string {
    const template = document.createElement("template");
    template.innerHTML = html;
    return template.content.textContent ?? "";
}

/**
 * The server row's markup for `username`, or null.
 *
 * Null unless the snapshot's own profile heading names this member. The snapshot map lives for the
 * SESSION, so after a soft navigation it can still hold the previous profile, and restoring that would
 * put someone else's rungs on this page (the "wrong page's content" direction in
 * AGENTS/frontend-islands-and-ssr.md). On the dashboard, which ships no server row of its own, a match
 * can only be the member's own profile, so it restores their own rungs.
 */
function serverAchievementsRowHtml(username: string): string | null {
    const heading = getSsrSnapshotSubtreeHtml(USER_PAGE_COMPONENT, PROFILE_HEADING_SELECTOR);
    if (heading === null || normalizeHandle(textOf(heading)) !== normalizeHandle(username)) {
        return null;
    }
    return getSsrSnapshotSubtreeHtml(USER_PAGE_COMPONENT, SERVER_ROW_SELECTOR);
}

export function ServerAchievementsRow({ username, restored }: { username: string; restored: boolean }) {
    const html = React.useMemo(() => serverAchievementsRowHtml(username), [username]);
    if (!html) {
        return null;
    }
    return (
        <div
            // Keeps the anchor the ACHIEVEMENT_EARNED notification links to (`/user/{name}#achievements`).
            id="achievements"
            className="mt-4 border-t border-border pt-3"
            data-playwright-id="profile-achievements-ssr-restored"
            {...(restored ? { "data-island-achievements-restored": "" } : {})}
            // Our own JTE output, escaped server-side and captured before mount. See ssrSnapshot.ts.
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
}
